/**
 * BusinessRulesBuilder - No-code visual rule builder
 * 
 * Features:
 * - Visual rule composition
 * - Condition builder (if X > Y then...)
 * - Approval workflow builder
 * - Real-time rule validation
 */

import React, { useState } from 'react';

interface BusinessRule {
  id: string;
  condition: {
    field: string;
    operator: '>' | '<' | '=' | '!=' | '>=' | '<=' | 'contains' | 'starts_with';
    value: any;
  };
  action: {
    type: 'require_approval' | 'reject' | 'notify' | 'escalate';
    approvalLevel?: 'manager' | 'finance' | 'admin' | 'hr';
    message?: string;
  };
  description: string;
  enabled: boolean;
}

interface BusinessRulesBuilderProps {
  rules: BusinessRule[];
  availableFields: string[];
  onRuleAdd: (rule: BusinessRule) => void;
  onRuleUpdate: (rule: BusinessRule) => void;
  onRuleDelete: (ruleId: string) => void;
}

export const BusinessRulesBuilder: React.FC<BusinessRulesBuilderProps> = ({
  rules,
  availableFields,
  onRuleAdd,
  onRuleUpdate,
  onRuleDelete
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newRule, setNewRule] = useState<Partial<BusinessRule>>({
    condition: {
      field: '',
      operator: '>',
      value: ''
    },
    action: {
      type: 'require_approval',
      approvalLevel: 'manager'
    },
    description: '',
    enabled: true
  });

  // Handle rule creation
  const handleAddRule = () => {
    if (!newRule.condition?.field || !newRule.description) {
      alert('Please fill in all required fields');
      return;
    }

    const rule: BusinessRule = {
      id: `rule-${Date.now()}`,
      condition: newRule.condition as BusinessRule['condition'],
      action: newRule.action as BusinessRule['action'],
      description: newRule.description,
      enabled: true
    };

    onRuleAdd(rule);
    setNewRule({
      condition: { field: '', operator: '>', value: '' },
      action: { type: 'require_approval', approvalLevel: 'manager' },
      description: '',
      enabled: true
    });
    setIsAdding(false);
  };

  return (
    <div className="business-rules-builder">
      <div className="rules-header">
        <h3>Business Rules</h3>
        <button onClick={() => setIsAdding(true)} className="btn-primary">
          Add Rule
        </button>
      </div>

      {/* Existing rules */}
      <div className="rules-list">
        {rules.map(rule => (
          <div key={rule.id} className="rule-card">
            <div className="rule-header">
              <div className="rule-description">{rule.description}</div>
              <div className="rule-actions">
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={(e) => onRuleUpdate({ ...rule, enabled: e.target.checked })}
                  />
                  <span>Enabled</span>
                </label>
                <button
                  onClick={() => onRuleDelete(rule.id)}
                  className="btn-icon-danger"
                >
                  🗑️
                </button>
              </div>
            </div>

            <div className="rule-condition">
              <span className="label">If:</span>
              <span className="condition-text">
                {rule.condition.field} {rule.condition.operator} {rule.condition.value}
              </span>
            </div>

            <div className="rule-action">
              <span className="label">Then:</span>
              <span className="action-text">
                {rule.action.type === 'require_approval' && (
                  <>Require {rule.action.approvalLevel} approval</>
                )}
                {rule.action.type === 'reject' && <>Reject transaction</>}
                {rule.action.type === 'notify' && <>Send notification</>}
                {rule.action.type === 'escalate' && <>Escalate to {rule.action.approvalLevel}</>}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Add rule form */}
      {isAdding && (
        <div className="rule-form">
          <h4>Add New Rule</h4>

          <div className="form-field">
            <label>Rule Description</label>
            <input
              type="text"
              placeholder="e.g., High value transaction approval"
              value={newRule.description}
              onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
            />
          </div>

          <div className="form-section">
            <h5>Condition (If...)</h5>
            <div className="condition-builder">
              <select
                value={newRule.condition?.field}
                onChange={(e) => setNewRule({
                  ...newRule,
                  condition: { ...newRule.condition!, field: e.target.value }
                })}
              >
                <option value="">Select field...</option>
                {availableFields.map(field => (
                  <option key={field} value={field}>{field}</option>
                ))}
              </select>

              <select
                value={newRule.condition?.operator}
                onChange={(e) => setNewRule({
                  ...newRule,
                  condition: { ...newRule.condition!, operator: e.target.value as any }
                })}
              >
                <option value=">">Greater than</option>
                <option value="<">Less than</option>
                <option value="=">Equals</option>
                <option value="!=">Not equals</option>
                <option value=">=">Greater than or equal</option>
                <option value="<=">Less than or equal</option>
                <option value="contains">Contains</option>
                <option value="starts_with">Starts with</option>
              </select>

              <input
                type="text"
                placeholder="Value"
                value={newRule.condition?.value}
                onChange={(e) => setNewRule({
                  ...newRule,
                  condition: { ...newRule.condition!, value: e.target.value }
                })}
              />
            </div>
          </div>

          <div className="form-section">
            <h5>Action (Then...)</h5>
            <div className="action-builder">
              <select
                value={newRule.action?.type}
                onChange={(e) => setNewRule({
                  ...newRule,
                  action: { ...newRule.action!, type: e.target.value as any }
                })}
              >
                <option value="require_approval">Require Approval</option>
                <option value="reject">Reject Transaction</option>
                <option value="notify">Send Notification</option>
                <option value="escalate">Escalate</option>
              </select>

              {(newRule.action?.type === 'require_approval' || newRule.action?.type === 'escalate') && (
                <select
                  value={newRule.action?.approvalLevel}
                  onChange={(e) => setNewRule({
                    ...newRule,
                    action: { ...newRule.action!, approvalLevel: e.target.value as any }
                  })}
                >
                  <option value="manager">Manager</option>
                  <option value="finance">Finance</option>
                  <option value="admin">Admin</option>
                  <option value="hr">HR</option>
                </select>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button onClick={handleAddRule} className="btn-primary">
              Add Rule
            </button>
            <button onClick={() => setIsAdding(false)} className="btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {rules.length === 0 && !isAdding && (
        <div className="empty-state">
          <p>No business rules defined yet. Add rules to control workflow behavior.</p>
        </div>
      )}
    </div>
  );
};
